import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Users,
  CreditCard,
  TrendingUp,
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  Shield,
  LogOut,
  RefreshCcw,
} from 'lucide-react';

interface BusinessConfig {
  id: string;
  user_id: string;
  biz_name: string;
  biz_type: string;
  subscription_status: 'trial' | 'active' | 'expired' | 'cancelled';
  plan: 'starter' | 'pro';
  trial_ends_at: string;
  stripe_customer_id?: string;
  whop_membership_id?: string;
  ndis_verified: boolean;
  contact_email: string;
  contact_phone: string;
  created_at: string;
  onboarding_completed: boolean;
  discount_percent?: number;
  discount_reason?: string;
}

interface Stats {
  totalBusinesses: number;
  activeSubscriptions: number;
  trialUsers: number;
  expiredSubscriptions: number;
  monthlyRevenue: number;
  starterPlan: number;
  proPlan: number;
}

export function SuperAdminDashboard() {
  const [businesses, setBusinesses] = useState<BusinessConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'trial' | 'expired' | 'cancelled'>('all');
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());
  const [stats, setStats] = useState<Stats>({
    totalBusinesses: 0,
    activeSubscriptions: 0,
    trialUsers: 0,
    expiredSubscriptions: 0,
    monthlyRevenue: 0,
    starterPlan: 0,
    proPlan: 0,
  });

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const fetchBusinesses = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('business_config')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setBusinesses(data || []);

      // Calculate stats
      const statsData: Stats = {
        totalBusinesses: data?.length || 0,
        activeSubscriptions: data?.filter(b => b.subscription_status === 'active').length || 0,
        trialUsers: data?.filter(b => b.subscription_status === 'trial').length || 0,
        expiredSubscriptions: data?.filter(b => b.subscription_status === 'expired' || b.subscription_status === 'cancelled').length || 0,
        monthlyRevenue: (data?.filter(b => b.subscription_status === 'active' && b.plan === 'starter').length || 0) * 19 +
          (data?.filter(b => b.subscription_status === 'active' && b.plan === 'pro').length || 0) * 49,
        starterPlan: data?.filter(b => b.plan === 'starter').length || 0,
        proPlan: data?.filter(b => b.plan === 'pro').length || 0,
      };

      setStats(statsData);
    } catch (error) {
      console.error('Error fetching businesses:', error);
      toast.error('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubscription = async (businessId: string, updates: Partial<BusinessConfig>) => {
    setUpdatingIds(prev => new Set(prev).add(businessId));
    try {
      const { error } = await supabase
        .from('business_config')
        .update(updates)
        .eq('id', businessId);

      if (error) throw error;

      toast.success('Business updated successfully');
      await fetchBusinesses();
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update business');
    } finally {
      setUpdatingIds(prev => {
        const next = new Set(prev);
        next.delete(businessId);
        return next;
      });
    }
  };

  const handleGrantFreeAccess = async (businessId: string) => {
    if (!confirm('Grant 100% discount (free access) to this business? This will activate their subscription immediately.')) return;

    await handleUpdateSubscription(businessId, {
      discount_percent: 100,
      discount_reason: 'Complimentary - Superadmin granted',
      subscription_status: 'active',
    });
  };

  const handleExtendTrial = async (businessId: string, days: number) => {
    const business = businesses.find(b => b.id === businessId);
    if (!business) return;

    const currentTrialEnd = business.trial_ends_at ? new Date(business.trial_ends_at) : new Date();
    const newTrialEnd = new Date(currentTrialEnd);
    newTrialEnd.setDate(newTrialEnd.getDate() + days);

    await handleUpdateSubscription(businessId, {
      trial_ends_at: newTrialEnd.toISOString(),
      subscription_status: 'trial',
    });
  };

  const filteredBusinesses = businesses.filter(business => {
    const matchesSearch =
      business.biz_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      business.contact_email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = filter === 'all' || business.subscription_status === filter;

    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'trial':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'expired':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'cancelled':
        return 'bg-slate-50 text-slate-600 border-slate-200';
      default:
        return 'bg-slate-50 text-slate-600 border-slate-200';
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast.success('Logged out successfully');
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="font-black uppercase text-slate-600">Loading SaaS Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b-2 border-slate-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-purple-600" />
              <div>
                <h1 className="text-2xl font-black text-slate-900 uppercase">VaultStack SaaS Manager</h1>
                <p className="text-xs text-slate-500 font-bold uppercase">Platform Administration</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={fetchBusinesses}
                variant="outline"
                className="gap-2 rounded-xl"
              >
                <RefreshCcw className="w-4 h-4" />
                Refresh
              </Button>
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="gap-2"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <StatCard
            icon={Users}
            label="Total Businesses"
            value={stats.totalBusinesses.toString()}
            color="text-purple-600"
          />
          <StatCard
            icon={CheckCircle2}
            label="Active"
            value={stats.activeSubscriptions.toString()}
            color="text-green-600"
          />
          <StatCard
            icon={Clock}
            label="Trial Users"
            value={stats.trialUsers.toString()}
            color="text-amber-600"
          />
          <StatCard
            icon={XCircle}
            label="Expired/Cancelled"
            value={stats.expiredSubscriptions.toString()}
            color="text-red-600"
          />
          <StatCard
            icon={CreditCard}
            label="Monthly Revenue"
            value={`$${stats.monthlyRevenue}`}
            color="text-emerald-600"
          />
          <StatCard
            icon={TrendingUp}
            label="Starter Plans"
            value={stats.starterPlan.toString()}
            color="text-blue-600"
          />
          <StatCard
            icon={TrendingUp}
            label="Pro Plans"
            value={stats.proPlan.toString()}
            color="text-indigo-600"
          />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border-2 border-slate-100 p-4 shadow-sm">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by business name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 rounded-xl border-slate-200 font-bold"
              />
            </div>
            <div className="flex gap-2">
              {(['all', 'active', 'trial', 'expired', 'cancelled'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-2 rounded-xl font-black uppercase text-[10px] tracking-widest transition-all ${
                    filter === f
                      ? 'bg-slate-900 text-white shadow-lg'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Businesses Table */}
        <div className="bg-white rounded-2xl border-2 border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b-2 border-slate-100">
                <tr>
                  <th className="text-left p-4 text-[10px] font-black text-slate-500 uppercase">Business</th>
                  <th className="text-left p-4 text-[10px] font-black text-slate-500 uppercase">Contact</th>
                  <th className="text-left p-4 text-[10px] font-black text-slate-500 uppercase">Status</th>
                  <th className="text-left p-4 text-[10px] font-black text-slate-500 uppercase">Plan</th>
                  <th className="text-left p-4 text-[10px] font-black text-slate-500 uppercase">Discount</th>
                  <th className="text-left p-4 text-[10px] font-black text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredBusinesses.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-12 text-center text-slate-500 font-bold">
                      No businesses found
                    </td>
                  </tr>
                ) : (
                  filteredBusinesses.map((business) => (
                    <tr key={business.id} className="hover:bg-slate-50 transition-colors">
                      <td className="p-4">
                        <p className="font-black text-slate-900">{business.biz_name}</p>
                        <p className="text-xs text-slate-500 uppercase font-bold">{business.biz_type}</p>
                      </td>
                      <td className="p-4">
                        <p className="text-sm font-bold text-slate-700">{business.contact_email}</p>
                        <p className="text-xs text-slate-500">{business.contact_phone}</p>
                      </td>
                      <td className="p-4">
                        <span className={`text-[8px] font-black px-2 py-1 rounded uppercase border ${getStatusColor(business.subscription_status)}`}>
                          {business.subscription_status}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`text-[8px] font-black px-2 py-1 rounded uppercase ${
                          business.plan === 'pro'
                            ? 'bg-indigo-50 text-indigo-700'
                            : 'bg-blue-50 text-blue-700'
                        }`}>
                          {business.plan}
                        </span>
                        {business.ndis_verified && (
                          <span className="ml-2 text-[8px] font-black bg-pink-50 text-pink-700 px-2 py-1 rounded uppercase">
                            NDIS
                          </span>
                        )}
                      </td>
                      <td className="p-4 min-w-[200px]">
                        <div className="space-y-2">
                          <select
                            value={business.discount_percent || 0}
                            onChange={(e) => handleUpdateSubscription(business.id, {
                              discount_percent: parseInt(e.target.value),
                              discount_reason: parseInt(e.target.value) === 50 ? 'NDIS Verified' :
                                               parseInt(e.target.value) === 100 ? 'Complimentary' : ''
                            })}
                            disabled={updatingIds.has(business.id)}
                            className="text-[8px] font-black rounded-lg border-slate-200 h-8 px-2 disabled:opacity-50"
                          >
                            <option value="0">No discount</option>
                            <option value="50">50% — NDIS/PWD</option>
                            <option value="100">100% — Complimentary</option>
                          </select>
                          <Input
                            value={business.discount_reason || ''}
                            onChange={(e) => handleUpdateSubscription(business.id, { discount_reason: e.target.value })}
                            disabled={updatingIds.has(business.id)}
                            placeholder="Discount reason..."
                            className="h-7 text-[9px] rounded-lg disabled:opacity-50"
                          />
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleExtendTrial(business.id, 7)}
                            disabled={updatingIds.has(business.id)}
                            className="text-[9px] font-black uppercase rounded-xl disabled:opacity-50"
                          >
                            +7 Days
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleExtendTrial(business.id, 30)}
                            disabled={updatingIds.has(business.id)}
                            className="text-[9px] font-black uppercase rounded-xl disabled:opacity-50"
                          >
                            +30 Days
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleUpdateSubscription(business.id, { subscription_status: 'active' })}
                            disabled={updatingIds.has(business.id)}
                            className="text-[9px] font-black uppercase rounded-xl bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                          >
                            Activate
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdateSubscription(business.id, { subscription_status: 'expired' })}
                            disabled={updatingIds.has(business.id)}
                            className="text-[9px] font-black uppercase rounded-xl text-red-600 border-red-200 disabled:opacity-50"
                          >
                            Expire
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleGrantFreeAccess(business.id)}
                            disabled={updatingIds.has(business.id)}
                            className="text-[9px] font-black uppercase rounded-xl bg-pink-500 hover:bg-pink-600 text-white disabled:opacity-50"
                          >
                            Grant Free
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

// Stat Card Component
function StatCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div className="bg-white p-4 rounded-2xl border-2 border-slate-100 shadow-sm">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-5 h-5 ${color}`} />
        <p className="text-[9px] font-black text-slate-500 uppercase">{label}</p>
      </div>
      <p className={`text-2xl font-black ${color}`}>{value}</p>
    </div>
  );
}
